import React, {FC} from 'react';
import styled from 'styled-components';

import {extractInitials} from '../../../../libs/shared/helpers/src/utilities/stringHelpers';
import {VisualSizesEnum} from '../../../../libs/shared/ui/src/styles/commonStyles';
import {boxShadow} from '../../../../libs/shared/ui/src/theme/themeStyleHelpers';
import {UserThemeConsumer} from '../../../../libs/shared/ui/src/theme/userThemeConsumer';
import {AccountModel} from '../../../models/api/accountModel';
import {CardModel} from '../../../models/api/cardModel';
import {VisualContactModel} from '../../../models/api/visualContactModel';
import {AccountAvatar} from '../../common/avatars/accountAvatar';
import {Avatar} from '../../common/avatars/avatar';
import {AvatarBackground} from '../../common/avatars/avatarBackground';
import {AvatarInitials} from '../../common/avatars/avatarInitials';
import {CardAvatar} from '../../common/avatars/cardAvatar';
import {Skeleton} from '../../common/skeleton';

/*
 * Constants.
 */

const accountAvatarIconSize = 12; // px.

/*
 * Props.
 */

interface ContactCardHeaderAvatarProps {
  visualContact: VisualContactModel;
  card?: CardModel;
  account?: AccountModel;
  isLoading: boolean;
  isAccountLoading: boolean;
}

/*
 * Style.
 */

const StyledAvatarWrapperDiv = styled.div`
  position: relative;
  flex: 0 0 48px;
`;

const StyledAccountAvatarWrapperDiv = styled.div`
  position: absolute;
  bottom: -2px;
  right: -2px;
`;

const StyledSkeleton = styled(Skeleton)`
  ${(p) => boxShadow(p.theme, `0 0 0 2px ${p.theme.greys.white}`)};
  top: 3px;
`;

/*
 * Component.
 */

export const ContactCardHeaderAvatar: FC<ContactCardHeaderAvatarProps> = (props) => {
  const {isLoading, isAccountLoading, visualContact, card, account} = props;

  if (isLoading) {
    return (
      <StyledAvatarWrapperDiv>
        <Skeleton height="48px" width="48px" borderRadius="50%" />
      </StyledAvatarWrapperDiv>
    );
  }

  return (
    <StyledAvatarWrapperDiv>
      {renderAvatar(visualContact, card)}
      {maybeRenderAccountAvatar(isAccountLoading, account)}
    </StyledAvatarWrapperDiv>
  );
};

/*
 * Helpers.
 */

function renderAvatar(visualContact: VisualContactModel, card?: CardModel) {
  if (card) {
    return <CardAvatar card={card} size={VisualSizesEnum.EXTRA_LARGE} />;
  }

  const initials = extractInitials(visualContact.name ?? visualContact.contact.handle);
  return (
    <UserThemeConsumer>
      {(theme) => (
        <Avatar size={VisualSizesEnum.EXTRA_LARGE}>
          <AvatarBackground color={theme.greys.shade70} />
          <AvatarInitials
            size={VisualSizesEnum.EXTRA_LARGE}
            textColor={theme.invertedText}
            value={initials}
          />
        </Avatar>
      )}
    </UserThemeConsumer>
  );
}

function maybeRenderAccountAvatar(isAccountLoading: boolean, account?: AccountModel) {
  if (isAccountLoading) {
    return (
      <StyledAccountAvatarWrapperDiv>
        <StyledSkeleton height="20px" width="20px" borderRadius="50%" />
      </StyledAccountAvatarWrapperDiv>
    );
  }
  if (!account) {
    return null;
  }
  return (
    <UserThemeConsumer>
      {(theme) => (
        <StyledAccountAvatarWrapperDiv>
          <AccountAvatar
            account={account}
            size={VisualSizesEnum.MEDIUM}
            avatarIconSize={accountAvatarIconSize}
            borderColor={theme.greys.white}
            avatarIconColor={theme.greys.shade70}
          />
        </StyledAccountAvatarWrapperDiv>
      )}
    </UserThemeConsumer>
  );
}
